import { useEffect, useState } from "react";

import { IconSquareCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Checkbox, Text } from "components";
import { DashboardService } from "services";
import { DashboardAgentResponse, DashboardScope } from "types";

import classes from "./AgentsSection.module.css";

interface AgentsSectionProps {
  className?: string;
  scope?: DashboardScope;
  selectedAgentIds?: number[] | null;
  onSelectionChange?: (agentIds: number[] | null) => void;
}

type AgentStatusVariant = "ACTIVE" | "INACTIVE" | "ON_BREAK" | "UNKNOWN";

const BREAK_STATUSES = ["BREAK", "ON_BREAK", "PAUSED", "ON_PAUSE"];

const normalizeAgentStatus = (status?: string): AgentStatusVariant => {
  if (!status) {
    return "UNKNOWN";
  }

  if (BREAK_STATUSES.includes(status)) {
    return "ON_BREAK";
  }

  if (status === "ACTIVE") {
    return "ACTIVE";
  }

  if (status === "INACTIVE") {
    return "INACTIVE";
  }

  return "UNKNOWN";
};

const formatYtdVolume = (value: number, locale: string) => {
  if (Math.abs(value) >= 1_000_000) {
    const valueInMillions = value / 1_000_000;
    const hasFraction = !Number.isInteger(valueInMillions);

    return `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: hasFraction ? 1 : 0,
      maximumFractionDigits: 1,
    }).format(valueInMillions)} M`;
  }

  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
};

const formatPayoutWeight = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(value);

const formatMp = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

const AgentsSection = ({
  className,
  scope = "admin",
  selectedAgentIds,
  onSelectionChange,
}: AgentsSectionProps) => {
  const { t, i18n } = useTranslation();
  const [agents, setAgents] = useState<DashboardAgentResponse[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const locale = i18n.language || "sr";
  const isSelectable = typeof onSelectionChange === "function";
  const resolvedSelectedAgentIds = isSelectable
    ? selectedAgentIds === null
      ? agents.map((agent) => agent.agentId)
      : selectedAgentIds ?? []
    : [];
  const selectedAgentsCount = resolvedSelectedAgentIds.length;
  const areAllAgentsSelected = isSelectable
    && agents.length > 0
    && selectedAgentsCount === agents.length;

  const normalizeSelectedAgentIds = (nextSelectedAgentIds: number[], allAgentIds: number[]) =>
    nextSelectedAgentIds.length === allAgentIds.length
      ? null
      : nextSelectedAgentIds;

  useEffect(() => {
    let mounted = true;

    const loadAgents = async () => {
      try {
        const data = await DashboardService.getAgents(scope);

        if (!mounted) {
          return;
        }

        setAgents(data ?? []);
        setHasLoaded(true);
      } catch {
        if (!mounted) {
          return;
        }

        setAgents([]);
        setHasLoaded(true);
      }
    };

    loadAgents();

    return () => {
      mounted = false;
    };
  }, [scope]);

  const handleSelectAll = () => {
    if (!isSelectable || !onSelectionChange) {
      return;
    }

    onSelectionChange(null);
  };

  const handleAgentToggle = (agentId: number) => {
    if (!isSelectable || !onSelectionChange) {
      return;
    }

    const allAgentIds = agents.map((agent) => agent.agentId);

    if (selectedAgentIds === null) {
      if (allAgentIds.length <= 1) {
        return;
      }

      onSelectionChange(allAgentIds.filter((currentAgentId) => currentAgentId !== agentId));
      return;
    }

    const isSelected = resolvedSelectedAgentIds.includes(agentId);

    if (isSelected) {
      if (resolvedSelectedAgentIds.length <= 1) {
        return;
      }

      onSelectionChange(
        resolvedSelectedAgentIds.filter((currentAgentId) => currentAgentId !== agentId),
      );
      return;
    }

    onSelectionChange(
      normalizeSelectedAgentIds(
        allAgentIds.filter(
          (currentAgentId) => resolvedSelectedAgentIds.includes(currentAgentId) || currentAgentId === agentId,
        ),
        allAgentIds,
      ),
    );
  };

  return (
    <section className={[classes.root, className].filter(Boolean).join(" ")}>
      {isSelectable && agents.length > 0 ? (
        <div className={classes.selectionBar}>
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={areAllAgentsSelected}
            className={classes.selectAllButton}
          >
            {t("dashboard.admin.agents.actions.selectAll")}
          </button>

          <Text className={classes.selectionSummary}>
            {t("dashboard.admin.agents.selection.summary", {
              selected: selectedAgentsCount,
              total: agents.length,
            })}
          </Text>
        </div>
      ) : null}

      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={`${classes.headerCell} ${classes.nameColumn}`}>
                {t("dashboard.admin.agents.columns.name")}
              </th>
              <th className={`${classes.headerCell} ${classes.statusColumn}`}>
                {t("dashboard.admin.agents.columns.status")}
              </th>
              <th className={`${classes.headerCell} ${classes.ytdColumn}`}>
                {t("dashboard.admin.agents.columns.ytdVolume")}
              </th>
              <th className={`${classes.headerCell} ${classes.payoutColumn}`}>
                {t("dashboard.admin.agents.columns.payoutWeight")}
              </th>
              <th className={`${classes.headerCell} ${classes.mpColumn}`}>
                {t("dashboard.admin.agents.columns.mp")}
              </th>
            </tr>
          </thead>

          <tbody>
            {agents.map((agent) => {
              const isSelected = !isSelectable || resolvedSelectedAgentIds.includes(agent.agentId);
              const normalizedStatus = normalizeAgentStatus(agent.status);
              const statusLabel =
                normalizedStatus === "ACTIVE"
                  ? t("agentStatus.active")
                  : normalizedStatus === "INACTIVE"
                    ? t("agentStatus.inactive")
                    : normalizedStatus === "ON_BREAK"
                      ? t("agentStatus.onBreak")
                      : agent.status;
              const statusDotClassName =
                normalizedStatus === "ACTIVE"
                  ? classes.statusDotActive
                  : normalizedStatus === "INACTIVE"
                    ? classes.statusDotInactive
                    : normalizedStatus === "ON_BREAK"
                      ? classes.statusDotOnBreak
                      : classes.statusDotUnknown;

              return (
                <tr
                  key={agent.agentId}
                  onClick={isSelectable ? () => handleAgentToggle(agent.agentId) : undefined}
                  className={[
                    classes.row,
                    isSelectable ? classes.rowSelectable : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <td className={classes.bodyCell}>
                    <div className={classes.nameCellContent}>
                      {isSelectable ? (
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleAgentToggle(agent.agentId)}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={agent.name}
                          className={classes.selectionCheckbox}
                          color="#71bf44"
                          size="xs"
                          label=""
                          mb={0}
                        />
                      ) : (
                        <IconSquareCheck
                          className={classes.nameMarker}
                          size={20}
                          stroke={1.6}
                          aria-hidden="true"
                        />
                      )}
                      <Text className={classes.nameText}>
                        {`${agent.name}${agent.teamLead ? " (TL)" : ""}`}
                      </Text>
                    </div>
                  </td>

                  <td className={classes.bodyCell}>
                    <div className={classes.statusCellContent}>
                      <span
                        className={`${classes.statusDot} ${statusDotClassName}`}
                        aria-hidden="true"
                      />
                      <Text className={classes.statusText}>{statusLabel}</Text>
                    </div>
                  </td>

                  <td className={classes.bodyCell}>
                    <Text className={classes.valueText}>
                      {formatYtdVolume(agent.ytdVolume ?? 0, locale)}
                    </Text>
                  </td>

                  <td className={`${classes.bodyCell} ${classes.payoutValueCell}`}>
                    <Text className={classes.valueText}>
                      {formatPayoutWeight(agent.payoutWeight ?? 0, locale)}
                    </Text>
                  </td>

                  <td className={`${classes.bodyCell} ${classes.mpValueCell}`}>
                    <Text className={classes.valueText}>{formatMp(agent.mp ?? 0, locale)}</Text>
                  </td>
                </tr>
              );
            })}

            {hasLoaded && agents.length === 0 ? (
              <tr>
                <td className={classes.emptyCell} colSpan={5}>
                  <Text className={classes.emptyText}>{t("results.no")}</Text>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default AgentsSection;
